// src/pages/redress/ObjectionScreen.tsx
//
// Objection screen for the FCA Motor Finance Redress Scheme (M6-3,
// reworked in Batch F3 / P2-13 H9-admin):
//   - Reason type select (4 options)
//   - Sub-reasons per reason type — FLAT snake_case keys matching the
//     backend's OBJECTION_SUBREASONS map (the old nested camelCase blocks
//     were silently dropped by the service on save)
//   - Evidence checklist — per-reason lists from Screens copy.docx
//     (replaces the old generic 7-item list)
//   - Notes textarea
//   - Date chain: 5 read-only derived dates + 4 editable actual dates
//   - Actions: Save draft (PATCH .../objection/draft — no transition),
//     Register objection (POST .../objection — transitions
//     prd_sent_to_client -> client_objecting, gated), uphold & re-issue
//     PRD (gated)
//
// The §9.12 interest-suspension banner is GONE — the PS26/3 final rules
// contain no interest suspension and the backend no longer writes those
// keys (Batch G).

import React, { useMemo, useState } from 'react';
import {
  Agreement,
  AgreementDates,
  Objection,
  ObjectionReasonType,
  RedressCase,
  canTransition,
  redressApi,
} from '../../api/redress';
import {
  applyDisabled,
  bannerDangerStyle,
  bannerSuccessStyle,
  buttonPrimary,
  buttonSecondary,
  colors,
  fieldsetStyle,
  inputStyle,
  labelStackStyle,
  legendStyle,
} from './styles';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ObjectionScreenProps {
  claimId: string;
  redressCase: RedressCase;
  agreement: Agreement;
  agreementId: string;
  onCaseUpdated: (c: RedressCase) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REASON_TYPES: Array<{ value: ObjectionReasonType; label: string }> = [
  { value: 'unfair_relationship', label: 'Unfair relationship' },
  { value: 'loss_damages', label: 'Loss / damages' },
  { value: 'offer_calculation', label: 'Offer / calculation' },
  { value: 'arrears', label: 'Arrears' },
];

// Sub-reason keys are VERBATIM the backend's OBJECTION_SUBREASONS values
// (redress_models.py) — keep in sync; unknown keys are filtered server-side.
const SUB_REASONS: Record<
  ObjectionReasonType,
  Array<{ key: string; label: string }>
> = {
  unfair_relationship: [
    { key: 'adequate_disclosure', label: 'Adequate disclosure was provided' },
    { key: 'consumer_sophisticated', label: 'The consumer was sophisticated' },
    {
      key: 'tied_not_operated_in_practice',
      label: 'Tied arrangement not operated in practice',
    },
  ],
  loss_damages: [
    {
      key: 'no_better_deal_option_a_alternative_aprs',
      label: 'No better deal — Option A (alternative APRs available)',
    },
    {
      key: 'no_better_deal_option_b_broker_internal_processes',
      label: 'No better deal — Option B (broker internal processes)',
    },
  ],
  offer_calculation: [
    { key: 'incorrect_calculation', label: 'Incorrect calculation' },
  ],
  arrears: [
    { key: 'financial_hardship', label: 'Financial hardship' },
    { key: 'priority_debts', label: 'Priority debts' },
    {
      key: 'unresolved_dispute_on_arrears',
      label: 'Unresolved dispute on arrears',
    },
  ],
};

// Per-reason evidence checklists — extracted from Screens copy.docx
// ("Objection reason- …" / "Evidence needed" sections). Each reason type
// carries its own list; the old generic 7-item list is gone.
const EVIDENCE_CHECKLISTS: Record<
  ObjectionReasonType,
  Array<{ key: string; label: string }>
> = {
  unfair_relationship: [
    {
      key: 'primaryDisclosureEvidence',
      label:
        'Primary disclosure evidence — signed acknowledgements, ' +
        'personalised correspondence, or contemporaneous recordings of ' +
        'verbal disclosure',
    },
    {
      key: 'secondaryTemplateEvidence',
      label:
        'Secondary — template/standard process documents verified as ' +
        'genuinely in use at the time (version control, system logs, ' +
        'process manuals)',
    },
    {
      key: 'disclosureClearAndProminent',
      label:
        'Disclosure was clear and prominent (bold text, key term ' +
        'position, or signed consumer acknowledgement — small print is ' +
        'insufficient)',
    },
    {
      key: 'arrangementSpecificStandardMet',
      label:
        'Arrangement-specific standard met (DCA: rate–commission link; ' +
        'high commission: fact and amount; tied: plain-language ' +
        'exclusivity explanation)',
    },
    {
      key: 'consumerSophisticationEvidence',
      label:
        'Sophistication — clear, contemporaneous, customer-specific ' +
        'evidence (worked in motor finance, professional knowledge, or ' +
        'prior adequate disclosure with the same firm)',
    },
    {
      key: 'tiedTransactionalDistributionData',
      label:
        'Tied-not-operated — transactional data showing distribution of ' +
        'broker introductions across lenders in the same quarter',
    },
    {
      key: 'tiedSupportingPoliciesOrAttestations',
      label:
        'Tied-not-operated — internal policies, firm monitoring records, ' +
        'broker attestations, or simultaneous tied arrangements with ' +
        'other lenders',
    },
    {
      key: 'tiedIndependentAuditReport',
      label:
        'Tied-not-operated — independent audit report confirming the ' +
        'evidence supports the conclusion',
    },
    {
      key: 'fcaNotificationMade',
      label:
        'FCA notified within 6 weeks of the scheme effective date ' +
        '(required to rely on the tied-not-operated rebuttal)',
    },
  ],
  loss_damages: [
    {
      key: 'optionARateSheets',
      label:
        'Option A — dated, version-controlled lender rate sheets matched ' +
        "to the consumer's credit profile and loan amount",
    },
    {
      key: 'optionAPlatformScreenshots',
      label:
        'Option A — timestamped broker platform screenshots showing ' +
        'available offers',
    },
    {
      key: 'optionAOffersConsideredNotes',
      label:
        'Option A — timestamped emails or system notes confirming offers ' +
        'considered and rationale for selection',
    },
    {
      key: 'optionBInternalProcessRecords',
      label:
        'Option B — records of internal policies showing the broker ' +
        'systematically checked all lenders before placing business',
    },
    {
      key: 'noProhibitedEvidenceRelied',
      label:
        'Confirmed NOT relying on market-wide/average APR comparisons, ' +
        'retrospectively generated evidence, or generic statements',
    },
    {
      key: 'brokerNotTiedExclusively',
      label:
        'Confirmed the broker was not tied exclusively to one lender and ' +
        'had arrangements with other suitable lenders (rebuttal ' +
        'unavailable otherwise)',
    },
  ],
  offer_calculation: [
    {
      key: 'originalLoanAgreement',
      label:
        "The consumer's original loan agreement showing correct figures " +
        'for loan amount, term and repayments',
    },
    {
      key: 'independentRedressCalculation',
      label:
        'Independent calculation of the redress using the correct scheme ' +
        'methodology',
    },
    {
      key: 'incorrectFiguresCorrespondence',
      label:
        'Correspondence or data showing the firm has used incorrect ' +
        'figures or applied the wrong remedy',
    },
    {
      key: 'fcaCompensatoryRatesEvidence',
      label:
        'Evidence of the correct compensatory interest rates for the ' +
        'relevant years as published by the FCA in PS26/3',
    },
  ],
  arrears: [
    {
      key: 'priorityDebtsEvidence',
      label:
        'Financial hardship / priority debts — obligations whose ' +
        'non-payment risks the home, essential goods/services, court or ' +
        'statutory obligations, or utility supplies',
    },
    {
      key: 'disputeExistsEvidence',
      label:
        'Dispute exists — complaint letters/emails, firm acknowledgement ' +
        'or final response, FOS confirmation the complaint is open, or ' +
        'live court documents',
    },
    {
      key: 'disputeUnresolvedEvidence',
      label:
        'Dispute unresolved — no settlement or accepted redress offer, ' +
        'FOS confirms no determination, court records confirm no judgment',
    },
    {
      key: 'disputeRelatesToSetOffArrears',
      label:
        'Dispute relates to the specific arrears being set off — the ' +
        'disputed amount directly corresponds to the set-off arrears',
    },
  ],
};

// Read-only (derived) date fields per §6.2.
const DERIVED_DATES: Array<{ key: keyof AgreementDates; label: string; hint: string }> = [
  // P1-8 (H7): standardised anchor key. Legacy 'prdDate' is read as a
  // display fallback at the render site only, never as a deadline anchor.
  { key: 'provisionalRedressDate', label: 'PRD date', hint: 'PRD issued' },
  { key: 'objectionDueDate', label: 'Objection due', hint: 'PRD + 1 month' },
  { key: 'acknowledgementDueDate', label: 'Acknowledgement due', hint: 'objection + 7 days' },
  { key: 'formalObjectionDueDate', label: 'Formal objection due', hint: 'acknowledgement + 1 month' },
  { key: 'responseDueDate', label: 'Response due', hint: 'formal + 2 months' },
];

// Editable (actual) date fields.
const ACTUAL_DATES: Array<{ key: keyof AgreementDates; label: string }> = [
  { key: 'objectionDate', label: 'Objection date' },
  { key: 'acknowledgementDate', label: 'Acknowledgement date' },
  { key: 'formalObjectionDate', label: 'Formal objection date' },
  { key: 'responseDate', label: 'Response date' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractError(err: unknown, fallback: string): string {
  const ax = err as {
    response?: { data?: { error?: string; reason?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.error ||
    ax.response?.data?.reason ||
    ax.message ||
    fallback
  );
}

function cloneObjection(obj: Objection | undefined): Objection {
  const src: Objection = obj ?? {};
  return {
    reasonType: src.reasonType ?? null,
    subReasons: src.subReasons ? { ...src.subReasons } : {},
    evidenceChecklist: src.evidenceChecklist ? { ...src.evidenceChecklist } : {},
    evidenceBlobRefs: src.evidenceBlobRefs ? [...src.evidenceBlobRefs] : [],
    notes: src.notes ?? '',
  };
}

function cloneDates(dates: AgreementDates | undefined): AgreementDates {
  return { ...(dates ?? {}) };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ObjectionScreen({
  claimId,
  redressCase,
  agreement,
  agreementId,
  onCaseUpdated,
}: ObjectionScreenProps): React.ReactElement {
  const [draft, setDraft] = useState<Objection>(() =>
    cloneObjection(agreement.objection),
  );
  const [actualDates, setActualDates] = useState<AgreementDates>(() =>
    cloneDates(agreement.dates),
  );
  // F3 polish (backdated objections): the date the client actually
  // objected, sent as ``objectionDate`` with Register objection so the
  // derived deadline chain anchors on it. Defaults to today (matching
  // the backend's stamp-today-when-omitted behaviour).
  const [registerObjectionDate, setRegisterObjectionDate] = useState<string>(
    () => new Date().toISOString().slice(0, 10),
  );
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [registering, setRegistering] = useState<boolean>(false);
  const [savingDates, setSavingDates] = useState<boolean>(false);
  const [upholding, setUpholding] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const agreementStatus = agreement.workflow.status;
  const canUphold = agreementStatus === 'objection_submitted';
  // Registering the intent-to-object transitions
  // prd_sent_to_client -> client_objecting (record_objection guards it
  // server-side) — mirror the gate here (H10 pattern).
  const canRegister =
    canTransition(agreementStatus, 'client_objecting') &&
    Boolean(draft.reasonType);

  const uploadedCount = useMemo(
    () => agreement.objection?.evidenceBlobRefs?.length ?? 0,
    [agreement.objection],
  );

  const subReasonItems = draft.reasonType
    ? SUB_REASONS[draft.reasonType]
    : [];
  const evidenceItems = draft.reasonType
    ? EVIDENCE_CHECKLISTS[draft.reasonType]
    : [];

  // ---------------------------------------------------------------------------
  // Draft updaters
  // ---------------------------------------------------------------------------

  function setReasonType(rt: ObjectionReasonType | null): void {
    setDraft((prev) => {
      if (rt === prev.reasonType) return prev;
      // A different reason type has different sub-reason keys and a
      // different evidence list — stale ticks must not survive invisibly.
      return { ...prev, reasonType: rt, subReasons: {}, evidenceChecklist: {} };
    });
  }

  function setSubReason(key: string, val: boolean): void {
    setDraft((prev) => ({
      ...prev,
      subReasons: { ...(prev.subReasons ?? {}), [key]: val },
    }));
  }

  function setEvidenceItem(key: string, val: boolean): void {
    setDraft((prev) => ({
      ...prev,
      evidenceChecklist: { ...(prev.evidenceChecklist ?? {}), [key]: val },
    }));
  }

  function setNotes(val: string): void {
    setDraft((prev) => ({ ...prev, notes: val }));
  }

  function setActualDate(key: keyof AgreementDates, val: string): void {
    setActualDates((prev) => ({ ...prev, [key]: val || undefined }));
  }

  // The flat service-shape payload (H9-admin): reasonType + snake_case
  // subReasons bool map + evidenceChecklist + notes.
  function buildPayload(): {
    reasonType?: ObjectionReasonType | null;
    subReasons?: Record<string, boolean>;
    evidenceChecklist?: Record<string, boolean>;
    notes?: string;
  } {
    return {
      reasonType: draft.reasonType ?? null,
      subReasons: draft.subReasons ?? {},
      evidenceChecklist: draft.evidenceChecklist ?? {},
      notes: draft.notes ?? '',
    };
  }

  // ---------------------------------------------------------------------------
  // Network actions
  // ---------------------------------------------------------------------------

  const refreshFromCase = (updated: RedressCase): void => {
    onCaseUpdated(updated);
    const updatedAgreement = updated.agreements.find(
      (a) => a.agreementId === agreementId,
    );
    setDraft(cloneObjection(updatedAgreement?.objection));
  };

  // Non-transitioning draft save — allowed at any status.
  const handleSaveDraft = async (): Promise<void> => {
    setSavingDraft(true);
    setError(null);
    setMessage(null);
    try {
      const payload = buildPayload();
      const updated = await redressApi.saveObjectionDraft(
        claimId,
        redressCase.id,
        agreementId,
        // The draft route rejects an unknown reasonType — omit when unset.
        payload.reasonType
          ? payload
          : {
              subReasons: payload.subReasons,
              evidenceChecklist: payload.evidenceChecklist,
              notes: payload.notes,
            },
      );
      refreshFromCase(updated);
      setMessage('Objection draft saved (no status change).');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to save objection draft'));
    } finally {
      setSavingDraft(false);
    }
  };

  // Transitioning intake: registers the intent-to-object
  // (prd_sent_to_client -> client_objecting) and derives the date chain.
  const handleRegisterObjection = async (): Promise<void> => {
    setRegistering(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await redressApi.saveObjection(
        claimId,
        redressCase.id,
        agreementId,
        {
          ...buildPayload(),
          // Backdated objections: anchor the derived deadline chain on
          // the real objection date (route stamps today when omitted).
          objectionDate: registerObjectionDate || undefined,
        },
      );
      refreshFromCase(updated);
      setActualDates(
        cloneDates(
          updated.agreements.find((a) => a.agreementId === agreementId)?.dates,
        ),
      );
      setMessage('Objection registered — agreement is now Client objecting.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to register objection'));
    } finally {
      setRegistering(false);
    }
  };

  const handleSaveDates = async (): Promise<void> => {
    setSavingDates(true);
    setError(null);
    setMessage(null);
    // Build patch containing only the editable actual dates.
    const actualDatesPatch: Partial<AgreementDates> = {};
    for (const { key } of ACTUAL_DATES) {
      const val = actualDates[key];
      if (val !== undefined) {
        (actualDatesPatch as Record<string, string | undefined>)[key] = val;
      }
    }
    try {
      const updated = await redressApi.saveDates(
        claimId,
        redressCase.id,
        agreementId,
        actualDatesPatch,
      );
      onCaseUpdated(updated);
      const updatedAgreement = updated.agreements.find(
        (a) => a.agreementId === agreementId,
      );
      setActualDates(cloneDates(updatedAgreement?.dates));
      setMessage('Dates saved.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to save dates'));
    } finally {
      setSavingDates(false);
    }
  };

  const handleUphold = async (): Promise<void> => {
    setUpholding(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await redressApi.reissuePrd(
        claimId,
        redressCase.id,
        agreementId,
      );
      onCaseUpdated(updated);
      setMessage('Objection upheld — PRD re-issued.');
    } catch (err: unknown) {
      setError(extractError(err, 'Failed to uphold objection'));
    } finally {
      setUpholding(false);
    }
  };

  const anyBusy = savingDraft || registering || savingDates || upholding;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Reason type                                             */}
      {/* ------------------------------------------------------------------ */}
      <fieldset style={fieldsetStyle} aria-label="Reason type">
        <legend style={legendStyle}>Reason type</legend>
        <label style={labelStackStyle}>
          Objection reason type
          <select
            style={inputStyle}
            value={draft.reasonType ?? ''}
            onChange={(e) =>
              setReasonType((e.target.value as ObjectionReasonType) || null)
            }
          >
            <option value="">— select reason type —</option>
            {REASON_TYPES.map((rt) => (
              <option key={rt.value} value={rt.value}>
                {rt.label}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      {/* ------------------------------------------------------------------ */}
      {/* Section 2 — Sub-reasons (flat snake_case keys, per reason type)     */}
      {/* ------------------------------------------------------------------ */}
      {draft.reasonType ? (
        <fieldset
          style={fieldsetStyle}
          aria-label={`${draft.reasonType} sub-reasons`}
        >
          <legend style={legendStyle}>
            {REASON_TYPES.find((r) => r.value === draft.reasonType)?.label}
            {' — sub-reasons'}
          </legend>
          {draft.reasonType === 'loss_damages' ? (
            <div style={{ fontSize: 13, color: colors.neutral600, marginBottom: 10 }}>
              Note: Option A is not acceptable where equivalent rates were not
              available from the broker; Option B is not available where the
              broker did not have internal processes for APR adjustment.
              Neither is available where the broker was tied exclusively to
              one lender.
            </div>
          ) : null}
          <div style={checkboxGridStyle}>
            {subReasonItems.map((item) => (
              <CheckboxRow
                key={item.key}
                label={item.label}
                checked={Boolean(draft.subReasons?.[item.key])}
                onChange={(v) => setSubReason(item.key, v)}
              />
            ))}
          </div>
          {draft.reasonType === 'unfair_relationship' &&
          draft.subReasons?.tied_not_operated_in_practice ? (
            <div
              style={{
                marginTop: 10,
                padding: '8px 12px',
                background: colors.warningLight,
                border: `1px solid ${colors.warningBorder}`,
                borderRadius: 6,
                fontSize: 13,
                color: colors.neutral800,
              }}
              role="note"
            >
              <strong>FCA notification required</strong> within 6 weeks of
              scheme effective date.
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Section 3 — Evidence checklist (per-reason, Screens copy.docx)      */}
      {/* ------------------------------------------------------------------ */}
      <fieldset style={fieldsetStyle} aria-label="Evidence checklist">
        <legend style={legendStyle}>Evidence checklist</legend>
        <div style={{ fontSize: 13, color: colors.neutral600, marginBottom: 10 }}>
          {uploadedCount} file{uploadedCount !== 1 ? 's' : ''} uploaded (via client
          portal — read-only here).
        </div>
        {draft.reasonType ? (
          <div style={checkboxGridStyle}>
            {evidenceItems.map((item) => (
              <CheckboxRow
                key={item.key}
                label={item.label}
                checked={Boolean(draft.evidenceChecklist?.[item.key])}
                onChange={(v) => setEvidenceItem(item.key, v)}
              />
            ))}
          </div>
        ) : (
          <em style={{ color: colors.neutral500, fontSize: 13 }}>
            Select a reason type to see the evidence required for that
            objection ground.
          </em>
        )}
      </fieldset>

      {/* ------------------------------------------------------------------ */}
      {/* Section 4 — Notes                                                    */}
      {/* ------------------------------------------------------------------ */}
      <fieldset style={fieldsetStyle} aria-label="Notes">
        <legend style={legendStyle}>Notes</legend>
        <label style={labelStackStyle}>
          Objection notes
          <textarea
            rows={4}
            value={draft.notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            placeholder="Any additional context, correspondence reference, etc."
          />
        </label>
      </fieldset>

      {/* Draft save / register / uphold action row */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 20,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ ...labelStackStyle, maxWidth: 200 }}>
          Objection date (for registration)
          <input
            type="date"
            value={registerObjectionDate}
            onChange={(e) => setRegisterObjectionDate(e.target.value)}
            style={inputStyle}
            title="The date the client objected — backdate if the objection arrived earlier. Defaults to today."
          />
        </label>
        <button
          type="button"
          disabled={anyBusy}
          onClick={() => void handleSaveDraft()}
          style={applyDisabled(buttonPrimary, anyBusy)}
          aria-busy={savingDraft}
          title="Saves the objection details without changing the agreement status."
        >
          {savingDraft ? 'Saving…' : 'Save draft'}
        </button>

        <button
          type="button"
          disabled={anyBusy || !canRegister}
          onClick={() => void handleRegisterObjection()}
          style={applyDisabled(buttonSecondary, anyBusy || !canRegister)}
          aria-busy={registering}
          title={
            canRegister
              ? 'Registers the intent-to-object and moves the agreement to Client objecting.'
              : `Requires status 'prd_sent_to_client' and a reason type (current: ${agreementStatus})`
          }
        >
          {registering ? 'Registering…' : 'Register objection'}
        </button>

        <button
          type="button"
          disabled={anyBusy || !canUphold}
          onClick={() => void handleUphold()}
          style={applyDisabled(buttonSecondary, anyBusy || !canUphold)}
          aria-busy={upholding}
          title={
            !canUphold
              ? `Agreement must be in 'objection_submitted' status (current: ${agreementStatus})`
              : 'Uphold the objection and re-issue a PRD to the client'
          }
        >
          {upholding ? 'Processing…' : 'Uphold objection & re-issue PRD'}
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Section 5 — Date chain                                               */}
      {/* ------------------------------------------------------------------ */}
      <fieldset style={fieldsetStyle} aria-label="Date chain">
        <legend style={legendStyle}>Date chain</legend>

        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontWeight: 500,
              fontSize: 12,
              color: colors.neutral500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 8,
            }}
          >
            Derived dates (read-only)
          </div>
          <div style={datesGridStyle}>
            {DERIVED_DATES.map(({ key, label, hint }) => (
              <label key={key} style={labelStackStyle}>
                {label}
                <span style={{ fontSize: 11, color: colors.neutral500, marginTop: -2 }}>
                  {hint}
                </span>
                <input
                  type="date"
                  readOnly
                  value={
                    agreement.dates?.[key] ??
                    // Legacy display fallback for the PRD anchor only.
                    (key === 'provisionalRedressDate'
                      ? agreement.dates?.prdDate ?? ''
                      : '')
                  }
                  style={{ ...inputStyle, background: colors.neutral100, color: colors.neutral500 }}
                />
              </label>
            ))}
          </div>
        </div>

        <div>
          <div
            style={{
              fontWeight: 500,
              fontSize: 12,
              color: colors.neutral500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 8,
            }}
          >
            Actual dates (editable)
          </div>
          <div style={datesGridStyle}>
            {ACTUAL_DATES.map(({ key, label }) => (
              <label key={key} style={labelStackStyle}>
                {label}
                <input
                  type="date"
                  value={actualDates[key] ?? ''}
                  onChange={(e) => setActualDate(key, e.target.value)}
                  style={inputStyle}
                />
              </label>
            ))}
          </div>

          <button
            type="button"
            disabled={savingDates}
            onClick={() => void handleSaveDates()}
            style={{ ...applyDisabled(buttonPrimary, savingDates), marginTop: 12 }}
            aria-busy={savingDates}
          >
            {savingDates ? 'Saving…' : 'Save dates'}
          </button>
        </div>
      </fieldset>

      {/* Status banners */}
      {message !== null && (
        <div style={{ ...bannerSuccessStyle, marginTop: 12 }} role="status">
          {message}
        </div>
      )}
      {error !== null && (
        <div style={{ ...bannerDangerStyle, marginTop: 12 }} role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface CheckboxRowProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function CheckboxRow({
  label,
  checked,
  onChange,
}: CheckboxRowProps): React.ReactElement {
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontSize: 14,
        color: colors.neutral800,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Inline styles
// ---------------------------------------------------------------------------

const checkboxGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 10,
};

const datesGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};
